import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Configuration - يمكن نقلها لملف منفصل للإعدادات
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-EMV8F9SBQH';
const GTM_ID = process.env.REACT_APP_GTM_ID || 'GT-NCLV9T2C';

// Google Analytics tracking hook
export const useGoogleAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page views
    if (typeof window.gtag !== 'undefined') {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname + location.search
      });
    }
  }, [location]);

  // Track custom events
  const trackEvent = (eventName, parameters = {}) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', eventName, {
        event_category: parameters.category || 'engagement',
        event_label: parameters.label || '',
        value: parameters.value || 0,
        ...parameters
      });
    }

    // Also send to GTM if available
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: eventName,
        ...parameters
      });
    }
  };

  // Track e-commerce events
  const trackPurchase = (transactionData) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'purchase', {
        transaction_id: transactionData.transaction_id,
        value: transactionData.value,
        currency: transactionData.currency || 'SAR',
        items: transactionData.items || []
      });
    }

    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: transactionData.transaction_id,
          value: transactionData.value,
          currency: transactionData.currency || 'SAR',
          items: transactionData.items || []
        }
      });
    }
  };

  // Track user interactions
  const trackUserInteraction = (action, category = 'user_engagement') => {
    trackEvent('user_interaction', {
      event_category: category,
      event_label: action,
      page_title: document.title,
      page_location: window.location.href
    });
  };

  // Track search events
  const trackSearch = (searchTerm, resultsCount = 0) => {
    trackEvent('search', {
      search_term: searchTerm,
      event_category: 'site_search',
      custom_parameters: {
        results_count: resultsCount
      }
    });
  };

  // Track lab registration
  const trackLabRegistration = () => {
    trackEvent('lab_registration', {
      event_category: 'conversion',
      event_label: 'new_lab_signup'
    });
  };

  // Track product actions
  const trackProductAction = (action, productData) => {
    trackEvent(`product_${action}`, {
      event_category: 'ecommerce',
      event_label: productData.name || '',
      item_id: productData.id || '',
      item_name: productData.name || '',
      item_category: productData.category || '',
      price: productData.price || 0,
      currency: 'SAR'
    });
  };

  // Track subscription events
  const trackSubscription = (action, planType = 'badeel_plus') => {
    trackEvent(`subscription_${action}`, {
      event_category: 'subscription',
      event_label: planType,
      value: action === 'purchase' ? 2760 : 0, // سعر باقة بديل بلس
      currency: 'SAR'
    });
  };

  // Track exchange events
  const trackExchange = (action, exchangeData = {}) => {
    trackEvent(`exchange_${action}`, {
      event_category: 'exchange',
      event_label: exchangeData.type || 'product_exchange',
      value: exchangeData.value || 0,
      currency: 'SAR'
    });
  };

  return {
    trackEvent,
    trackPurchase,
    trackUserInteraction,
    trackSearch,
    trackLabRegistration,
    trackProductAction,
    trackSubscription,
    trackExchange
  };
};

// SEO and Analytics utilities
export const updatePageTitle = (title, description = '') => {
  document.title = title;
  
  // Update meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription && description) {
    metaDescription.setAttribute('content', description);
  }

  // Update Open Graph tags
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) {
    ogTitle.setAttribute('content', title);
  }

  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription && description) {
    ogDescription.setAttribute('content', description);
  }

  // Update Twitter tags
  const twitterTitle = document.querySelector('meta[name="twitter:title"]');
  if (twitterTitle) {
    twitterTitle.setAttribute('content', title);
  }

  const twitterDescription = document.querySelector('meta[name="twitter:description"]');
  if (twitterDescription && description) {
    twitterDescription.setAttribute('content', description);
  }
};

// Structured Data helpers
export const addStructuredData = (structuredData) => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(structuredData);
  document.head.appendChild(script);
};

export const removeStructuredData = () => {
  const scripts = document.querySelectorAll('script[type="application/ld+json"]');
  scripts.forEach(script => {
    if (script.text.includes('"@context"')) {
      script.remove();
    }
  });
};

export default useGoogleAnalytics;
